import { Link } from "react-router-dom";
import { useMemo, useState, type ReactNode } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  Plus,
  Sparkles,
  UserPlus,
} from "lucide-react";
import {
  localTimeZone,
  resolvePreferredCurrency,
  summarizeBalances,
} from "@repo/api-schema";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { MoneyAmount } from "../components/MoneyAmount";
import { GroupCard } from "../components/GroupCard";
import { useMe, useMyGroups } from "../api/hooks";
import type { GroupListItem } from "../api/types";

export function DashboardPage(): ReactNode {
  const { data, isLoading } = useMyGroups();
  const { data: me } = useMe();
  const groups = useMemo(() => data?.items ?? [], [data]);

  const displayCurrency = resolvePreferredCurrency(
    me?.preferredCurrency,
    localTimeZone(),
  );

  const { owed, owing } = useMemo(
    () => summarizeBalances(groups, displayCurrency),
    [groups, displayCurrency],
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-headline-lg">Dashboard</h1>
      </div>

      <section>
        {isLoading ? (
          <SummarySkeleton />
        ) : (
          <Card className="overflow-hidden bg-primary text-on-primary">
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-4 px-6 pb-4 pt-6 sm:px-8">
                <div>
                  <p className="text-label-sm text-on-primary/65">
                    Your balance
                  </p>
                  <p className="mt-1 text-body-md font-semibold text-on-primary/90">
                    {groups.length === 0
                      ? "No pending items"
                      : `Across ${groups.length} active group${groups.length === 1 ? "" : "s"}`}
                  </p>
                </div>
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-on-primary/12">
                  <Sparkles className="size-5" />
                </span>
              </div>
              <div className="grid gap-px bg-on-primary/15 sm:grid-cols-2">
                <BalanceStat
                  label="You are owed"
                  minor={owed}
                  currency={displayCurrency}
                  icon={ArrowUpRight}
                  detail={
                    owed === 0 ? "Nothing outstanding" : "Coming back to you"
                  }
                />
                <BalanceStat
                  label="You owe"
                  minor={owing}
                  currency={displayCurrency}
                  icon={ArrowDownRight}
                  detail={owing === 0 ? "All settled up" : "Still to pay"}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {!isLoading && groups.length === 0 ? (
        <EmptyState />
      ) : (
        <GroupsSection isLoading={isLoading} groups={groups} />
      )}
    </div>
  );
}

function SummarySkeleton(): ReactNode {
  return (
    <Card tone="ghost" className="overflow-hidden">
      <CardContent className="space-y-5 p-6">
        <Skeleton className="h-4 w-36" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-28 rounded-md" />
          <Skeleton className="h-28 rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

function BalanceStat({
  label,
  minor,
  currency,
  icon: Icon,
  detail,
}: {
  label: string;
  minor: number;
  currency: Parameters<typeof MoneyAmount>[0]["currency"];
  icon: typeof ArrowUpRight;
  detail: string;
}): ReactNode {
  return (
    <div className="bg-primary px-6 py-6 sm:px-8">
      <div className="flex items-center gap-2 text-on-primary/70">
        <span className="grid size-7 place-items-center rounded-full bg-on-primary/12">
          <Icon className="size-4" />
        </span>
        <p className="text-label-sm">{label}</p>
      </div>
      <MoneyAmount
        minor={minor}
        currency={currency}
        className="mt-4 block text-[clamp(2rem,9vw,3rem)] font-extrabold leading-none tracking-[-0.03em]"
      />
      <p className="mt-3 text-label-md text-on-primary/70">{detail}</p>
    </div>
  );
}

function EmptyState(): ReactNode {
  return (
    <Card tone="ghost">
      <CardContent className="py-12 text-center">
        <div className="mx-auto grid size-16 place-items-center rounded-full bg-surface-container">
          <UserPlus className="size-7 text-primary" />
        </div>
        <h2 className="mt-6 text-headline-md">No active groups yet</h2>
        <p className="mx-auto mt-3 max-w-md text-body-md text-on-surface-variant">
          Tailspend works best when shared. Create a group for your roommates,
          trips, or projects to start tracking shared expenses effortlessly.
        </p>
        <div className="mt-6 flex justify-center">
          <Link to="/groups/new">
            <Button>
              <Plus className="size-4" />
              Create your first group
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * All of the user's groups. Groups with an outstanding balance show first as
 * a vertically-scrolling grid; fully settled-up groups (balance === 0) are
 * tucked into an optional, collapsed section below so the active ones stay
 * front and centre.
 */
function GroupsSection({
  isLoading,
  groups,
}: {
  isLoading: boolean;
  groups: GroupListItem[];
}): ReactNode {
  const [showSettled, setShowSettled] = useState(false);

  const { unsettled, settled } = useMemo(() => {
    const unsettled: GroupListItem[] = [];
    const settled: GroupListItem[] = [];
    for (const g of groups) (g.balance === 0 ? settled : unsettled).push(g);
    return { unsettled, settled };
  }, [groups]);

  if (isLoading) {
    return (
      <section className="space-y-4">
        <h2 className="text-headline-md">Groups</h2>
        <div className="grid gap-bento-gap md:grid-cols-3">
          <Skeleton className="h-40 rounded-md" />
          <Skeleton className="h-40 rounded-md" />
          <Skeleton className="h-40 rounded-md" />
        </div>
      </section>
    );
  }

  // Everything settled: no separate "active" grid, just the (expandable) list.
  const activeGroups = unsettled.length > 0 ? unsettled : settled;
  const collapsibleSettled = unsettled.length > 0 ? settled : [];

  return (
    <section className="space-y-4">
      <h2 className="text-headline-md">Groups</h2>

      <div className="grid gap-bento-gap md:grid-cols-3">
        {activeGroups.map((g) => (
          <GroupCard key={g.id} group={g} />
        ))}
      </div>

      {collapsibleSettled.length > 0 ? (
        <div className="space-y-4 pt-2">
          <button
            type="button"
            onClick={() => setShowSettled((v) => !v)}
            aria-expanded={showSettled}
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-outline-variant/50 px-4 py-3 text-left text-label-lg text-on-surface-variant transition-colors hover:bg-surface-container/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span>
              Settled up
              <span className="ml-2 text-on-surface-variant/70">
                {collapsibleSettled.length}
              </span>
            </span>
            <ChevronDown
              className={`size-5 transition-transform ${showSettled ? "rotate-180" : ""}`}
            />
          </button>
          {showSettled ? (
            <div className="grid gap-bento-gap md:grid-cols-3">
              {collapsibleSettled.map((g) => (
                <GroupCard key={g.id} group={g} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
